import React, { useState, useEffect, useRef } from 'react';
import { CloudRain, X, Send, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { checkBookingsWeather } from '../../utils/weather';
import { supabase } from '../../supabase';

export default function WeatherAlertBanner({ bookings }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());
  const [notifying, setNotifying] = useState({});
  const [notified, setNotified] = useState({});
  const [confirmId, setConfirmId] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (!bookings?.length || fetched.current) return;
    fetched.current = true;

    checkBookingsWeather(bookings)
      .then(setAlerts)
      .catch(err => console.warn('Weather alert check failed:', err.message))
      .finally(() => setLoading(false));
  }, [bookings]);

  const handleNotify = async (alert) => {
    const id = alert.booking.id;
    setNotifying(p => ({ ...p, [id]: true }));
    setConfirmId(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch('/api/weather-reschedule-notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bookingId: id,
          clientName: alert.booking.client_name,
          clientEmail: alert.booking.email,
          clientPhone: alert.booking.phone,
          scheduledAt: alert.booking.scheduled_at,
          address: alert.booking.address,
          weatherReason: alert.reason,
          weatherIcon: alert.icon,
        }),
      });

      if (res.ok) {
        setNotified(p => ({ ...p, [id]: true }));
      } else {
        const err = await res.json();
        alert(`Failed to send: ${err.error}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setNotifying(p => ({ ...p, [id]: false }));
    }
  };

  const visible = alerts.filter(a => !dismissed.has(a.booking.id));

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 bg-gray-50 rounded-xl px-4 py-3">
        <Loader2 size={14} className="animate-spin" />
        Checking weather for upcoming appointments…
      </div>
    );
  }

  if (!visible.length) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-1">
        <CloudRain size={16} className="text-blue-500" />
        <span className="text-sm font-semibold text-gray-700">
          Weather Alert{visible.length > 1 ? `s (${visible.length})` : ''}
        </span>
      </div>

      {visible.map(alert => {
        const id = alert.booking.id;
        const d = new Date(alert.booking.scheduled_at);
        const hasNotified = notified[id];
        const isNotifying = notifying[id];
        const isConfirming = confirmId === id;

        return (
          <div
            key={id}
            className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3"
          >
            <div className="flex items-start gap-3 min-w-0">
              <span className="text-xl shrink-0">{alert.icon}</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-800">
                  {alert.booking.client_name} — {format(d, 'EEE, MMM d')}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">{alert.reason}</p>
                <p className="text-xs text-gray-400">{alert.booking.address}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {hasNotified ? (
                <span className="text-xs text-emerald-600 font-medium">Notified ✓</span>
              ) : isConfirming ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Send reschedule notice?</span>
                  <button
                    onClick={() => handleNotify(alert)}
                    className="text-xs bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Yes, send
                  </button>
                  <button
                    onClick={() => setConfirmId(null)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  disabled={isNotifying}
                  onClick={() => setConfirmId(id)}
                  className="flex items-center gap-1.5 text-xs bg-white border border-blue-300 text-blue-700 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  {isNotifying ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Send size={12} />
                  )}
                  Notify Client
                </button>
              )}

              <button
                onClick={() => setDismissed(p => new Set([...p, id]))}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
